import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Lock, ArrowLeft, Home as HomeIcon, Smartphone, Link2, Settings2, CalendarClock, ShieldCheck, CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const INCLUDED = [
  { Icon: Smartphone, t: "App-installation", d: "Vi installerar och ställer in rätt app på din telefon så du är igång direkt." },
  { Icon: Link2, t: "Parkoppling", d: "Vi kopplar ihop låset med din telefon och ditt wifi – tryggt och korrekt." },
  { Icon: Settings2, t: "Genomgång av smarta funktioner", d: "Lås upp med kod, app eller fingeravtryck, aviseringar och gästkoder – förklarat enkelt." },
  { Icon: CalendarClock, t: "Anpassade scheman", d: "Egna koder och tider för t.ex. städhjälp, barn eller släkt – precis som din vardag ser ut." },
];

export default function DigitalLock() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", lock_model: "", message: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) { setError("Fyll i namn och telefonnummer."); return; }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/lock", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Digitalt lås – konfiguration och genomgång"
        description="Vi konfigurerar ditt smarta lås – app, parkoppling, behörigheter och gästkoder – och ger dig en pedagogisk genomgång. Vi monterar inte låset fysiskt."
        path="/digitala-las"
        jsonLd={serviceJsonLd({ name: "Digitalt lås – konfiguration", description: "Konfiguration och genomgång av digitala/smarta lås för konsumenter.", path: "/digitala-las" })}
      />
      <Link to="/" data-testid="lock-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <Lock size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Digitalt lås – tryggt och enkelt hemma
      </h1>
      <h2 className="font-heading text-lg md:text-xl font-semibold text-[#4A4A4A] mt-4 leading-relaxed">
        Vi konfigurerar ditt smarta lås och visar dig hur allt fungerar – i lugn takt och helt anpassat efter dig.
      </h2>

      {/* Value proposition */}
      <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-6 md:p-8 mt-8">
        <p className="text-lg md:text-xl leading-relaxed text-[#1A1A1A]">
          För dig som vill ha maximal trygghet hemma – utan krånglet. Vi ser till att ditt digitala lås
          gör vardagen enklare för hela familjen: släpp in barnen efter skolan, ge städhjälpen en egen
          kod och slipp oroa dig för borttappade nycklar.
        </p>
        <p className="text-base text-[#4A4A4A] mt-4 flex items-start gap-2">
          <ShieldCheck size={22} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-0.5" />
          Obs: Vi monterar inte låset fysiskt. Vi hjälper dig med det digitala – appar, inställningar,
          behörigheter och en pedagogisk genomgång så att du känner dig trygg med hur det fungerar.
        </p>
      </div>

      {/* What's included */}
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-10">Detta ingår</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        {INCLUDED.map(({ Icon, t, d }) => (
          <div key={t} className="bg-white border-2 border-gray-300 rounded-xl p-6 flex flex-col items-start">
            <span className="bg-blue-50 text-[#0056D2] rounded-xl p-3"><Icon size={30} strokeWidth={2} /></span>
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
            <p className="text-base text-[#4A4A4A] mt-2">{d}</p>
          </div>
        ))}
      </div>

      {/* Form */}
      {done ? (
        <div data-testid="lock-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-10 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din förfrågan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Jag har tagit emot dina uppgifter och återkommer så snart som möjligt för att boka en tid som passar dig.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Boka hjälp med ditt digitala lås</h2>
          <p className="text-base text-[#4A4A4A] mt-2">Fyll i dina uppgifter så återkommer jag och bokar en tid. Det tar bara en minut.</p>

          <div className="space-y-6 mt-6">
            <div>
              <label htmlFor="l-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
              <input id="l-name" data-testid="lock-name" value={form.name} onChange={set("name")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
            </div>
            <div>
              <label htmlFor="l-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-post</label>
              <input id="l-email" type="email" data-testid="lock-email" value={form.email} onChange={set("email")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
            </div>
            <div>
              <label htmlFor="l-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
              <input id="l-phone" type="tel" data-testid="lock-phone" value={form.phone} onChange={set("phone")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
            </div>
            <div>
              <label htmlFor="l-model" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Vilket lås har du? <span className="text-base font-medium text-[#4A4A4A]">(om du vet)</span></label>
              <input id="l-model" data-testid="lock-model" value={form.lock_model} onChange={set("lock_model")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="t.ex. Yale Doorman, Glue, Nuki eller vet ej" />
            </div>
            <div>
              <label htmlFor="l-message" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Meddelande / behov</label>
              <textarea id="l-message" data-testid="lock-message" value={form.message} onChange={set("message")} rows={5}
                className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                placeholder="Berätta gärna vad du vill ha hjälp med (valfritt)" />
            </div>

            {error && (
              <p data-testid="lock-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                <AlertCircle size={22} /> {error}
              </p>
            )}

            <button data-testid="lock-submit" type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
              {submitting ? "Skickar…" : "Skicka förfrågan"}
            </button>

            <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
              Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
